import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  adminLogin: (data) => api.post('/auth/admin/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  updatePassword: (data) => api.put('/auth/password', data),
  addAddress: (data) => api.post('/auth/address', data),
  deleteAddress: (addressId) => api.delete(`/auth/address/${addressId}`),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getOne: (slug) => api.get(`/products/${slug}`),
  getBySlug: (slug) => api.get(`/products/${slug}`),
  getById: (id) => api.get(`/products/id/${id}`),
  getFeatured: () => api.get('/products/featured'),
  getNewArrivals: () => api.get('/products/new-arrivals'),
  getBestSellers: () => api.get('/products/best-sellers'),
  search: (q) => api.get('/products/search', { params: { q } }),
  // Admin
  getAllAdmin: (params) => api.get('/products/admin/all', { params }),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  updateStock: (id, data) => api.put(`/products/${id}/stock`, data),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getFeatured: () => api.get('/categories/featured'),
  getOne: (slug) => api.get(`/categories/${slug}`),
  // Admin
  getAllAdmin: () => api.get('/categories/admin/all'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Cart API
export const cartAPI = {
  get: () => api.get('/cart'),
  add: (data) => api.post('/cart/add', data),
  update: (data) => api.put('/cart/update', data),
  remove: (itemId) => api.delete(`/cart/remove/${itemId}`),
  clear: () => api.delete('/cart/clear'),
  applyCoupon: (code) => api.post('/cart/coupon', { code }),
  removeCoupon: () => api.delete('/cart/coupon'),
};

// Wishlist API
export const wishlistAPI = {
  get: () => api.get('/wishlist'),
  add: (productId) => api.post('/wishlist/add', { productId }),
  remove: (productId) => api.delete(`/wishlist/remove/${productId}`),
  clear: () => api.delete('/wishlist/clear'),
  check: (productId) => api.get(`/wishlist/check/${productId}`),
};

// Orders API
export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  getAll: (params) => api.get('/orders', { params }),
  getMyOrders: () => api.get('/orders'),
  getOne: (id) => api.get(`/orders/${id}`),
  cancel: (id, reason) => api.put(`/orders/${id}/cancel`, { reason }),
  // Admin
  getAllAdmin: (params) => api.get('/orders/admin/all', { params }),
  getOneAdmin: (id) => api.get(`/orders/admin/${id}`),
  updateStatus: (id, data) => api.put(`/orders/${id}/status`, data),
  updatePayment: (id, data) => api.put(`/orders/${id}/payment`, data),
};

// Coupons API
export const couponsAPI = {
  validate: (code, subtotal) => api.post('/coupons/validate', { code, subtotal }),
  // Admin
  getAll: (params) => api.get('/coupons', { params }),
  getOne: (id) => api.get(`/coupons/${id}`),
  create: (data) => api.post('/coupons', data),
  update: (id, data) => api.put(`/coupons/${id}`, data),
  delete: (id) => api.delete(`/coupons/${id}`),
};

// Sliders API
export const slidersAPI = {
  getActive: () => api.get('/sliders'),
  // Admin
  getAll: () => api.get('/sliders/admin'),
  getOne: (id) => api.get(`/sliders/${id}`),
  create: (data) => api.post('/sliders', data),
  update: (id, data) => api.put(`/sliders/${id}`, data),
  delete: (id) => api.delete(`/sliders/${id}`),
  reorder: (sliders) => api.put('/sliders/reorder', { sliders }),
};

// Banners API
export const bannersAPI = {
  getByPosition: (position) => api.get('/banners', { params: { position } }),
  // Admin
  getAll: () => api.get('/banners/admin'),
  getOne: (id) => api.get(`/banners/${id}`),
  create: (data) => api.post('/banners', data),
  update: (id, data) => api.put(`/banners/${id}`, data),
  delete: (id) => api.delete(`/banners/${id}`),
};

// Reviews API
export const reviewsAPI = {
  getProductReviews: (productId, params) => api.get(`/reviews/product/${productId}`, { params }),
  getByProduct: (productId, params) => api.get(`/reviews/product/${productId}`, { params }),
  create: (data) => api.post('/reviews', data),
  update: (id, data) => api.put(`/reviews/${id}`, data),
  delete: (id) => api.delete(`/reviews/${id}`),
  vote: (id) => api.post(`/reviews/${id}/vote`),
  // Admin
  getAllAdmin: (params) => api.get('/reviews/admin', { params }),
  approve: (id, isApproved) => api.put(`/reviews/${id}/approve`, { isApproved }),
  deleteAdmin: (id) => api.delete(`/reviews/admin/${id}`),
};

// Users API (Admin)
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getOne: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getStats: () => api.get('/users/stats'),
};

// Dashboard API (Admin)
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRevenueChart: (period) => api.get('/dashboard/revenue-chart', { params: { period } }),
  getBestSellers: (limit) => api.get('/dashboard/best-sellers', { params: { limit } }),
  getRecentOrders: (limit) => api.get('/dashboard/recent-orders', { params: { limit } }),
  getOrderStatus: () => api.get('/dashboard/order-status'),
  getTopCategories: () => api.get('/dashboard/top-categories'),
};

// Upload API
export const uploadAPI = {
  uploadImage: (image, folder) => api.post('/upload', { image, folder }),
  deleteImage: (url) => api.delete('/upload', { data: { url } }),
};

export default api;
